import i18n from '../i18n';

/**
 * Rounds a number up to the nearest integer.
 * @param {number|string} value 
 * @returns {number}
 */
export const roundToUpper = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return 0;
  return Math.ceil(num);
};

/**
 * Formats a number according to Indian Standard (INR).
 *
 * Amounts are shown to the paisa, rounded half-up — the same rule the backend
 * uses for every stored total (policy P3-3). This used to CEIL to the rupee, so
 * a ₹1,234.10 challan displayed as ₹1,235.00 and never matched the PDF or Tally.
 * @param {number|string} amount
 * @param {Object} options
 * @returns {string}
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    round = true,
    showSymbol = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2
  } = options;

  let num = parseFloat(amount);
  if (isNaN(num)) return i18n.t('common.symbols.emDash');

  if (round) {
    num = Math.round((num + Number.EPSILON) * 100) / 100;
  }

  const formatted = num.toLocaleString('en-IN', {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return showSymbol ? `${i18n.t('common.symbols.currency')} ${formatted}` : formatted;
};

/**
 * Returns a default date range (start of current month to end of current month)
 * @returns {{from_date: string, to_date: string}}
 */
export const getDefaultDateRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  return {
    from_date: formatDate(firstDay),
    to_date: formatDate(lastDay),
  };
};

/**
 * Formats a detailed bulk delete breakdown message if count keys are present in the response.
 * @param {Object} response 
 * @param {string} defaultMessage 
 * @returns {string}
 */
export const formatBulkDeleteMessage = (response = {}, defaultMessage = '') => {
  const root = response?.data || response || {};
  // The selected-orders endpoint nests its counts under { sales: {...}, purchase: {...} }.
  // Flatten those in so the REAL counts are found — otherwise this read only the top
  // level, found nothing, and fell back to the caller's requested-count default, which
  // reported "N deleted" even when 0 were actually removed.
  const data = { ...root, ...(root.sales || {}), ...(root.purchase || {}) };
  const counts = [];

  const keys = [
    { key: 'sales_orders_deleted', label: 'Sales Orders' },
    { key: 'order_items_deleted', label: 'Order Items' },
    { key: 'delivery_sub_orders_deleted', label: 'Delivery Sub-orders' },
    { key: 'delivery_sub_order_items_deleted', label: 'Delivery Sub-order Items' },
    { key: 'purchase_orders_deleted', label: 'Purchase Orders' },
    { key: 'purchase_order_items_deleted', label: 'Purchase Order Items' },
    { key: 'purchase_advices_deleted', label: 'Purchase Advices' },
    { key: 'purchase_advice_items_deleted', label: 'Purchase Advice Items' },
  ];

  keys.forEach(({ key, label }) => {
    const val = data[key];
    if (typeof val === 'number' && val > 0) {
      counts.push(`${val} ${label}`);
    }
  });

  if (counts.length > 0) {
    return `Deleted successfully: ${counts.join(', ')}`;
  }

  // A real response carried the count keys but every one is 0 → nothing matched.
  // Report that honestly instead of the "N deleted" default built from selection size.
  if (keys.some(({ key }) => typeof data[key] === 'number')) {
    return 'Nothing was deleted — the selected items may already have been removed.';
  }

  return response?.message || data?.message || defaultMessage;
};

const format = {
  roundToUpper,
  formatCurrency,
  getDefaultDateRange,
  formatBulkDeleteMessage,
};

export default format;

